import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bibleService } from '../../../services/bible';
import { fetchDailyVerse, resolveHomeBookName } from '../../../services/dailyVerse';
import BfHeader, { useBfHeaderState } from './BfHeader';
import BfHomeVideoBackdrop from './BfHomeVideoBackdrop';
import BfHomeSpot from './BfHomeSpot';
import BfChapterSelector from './BfChapterSelector';
import BfMenuSheet from './BfMenuSheet';
import { bfHaptic } from './BfHooks';
import { prefetchGuestVerseInsights } from '../../../services/guestVerseInsight';

const VERSE_PREVIEW_MAX = 340;

function trimVersePreview(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (t.length <= VERSE_PREVIEW_MAX) return t;
  return `${t.slice(0, VERSE_PREVIEW_MAX - 1)}…`;
}

/**
 * BfHome — главная: стих дня с бэкенда, гостевой insight.
 */
export default function BfHome({
  translation = 'synodal',
  onOpenSearch,
}) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [dailyVerse, setDailyVerse] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [showChapterSelector, setShowChapterSelector] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [insightMode, setInsightMode] = useState(null);
  const { hidden, scrolled } = useBfHeaderState();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await bibleService.getBooks(translation);
        if (!cancelled) setBooks(list || []);
      } catch {
        /* no-op */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [translation]);

  useEffect(() => {
    let cancelled = false;
    setDailyLoading(true);
    (async () => {
      try {
        const data = await fetchDailyVerse(translation, { refresh: true });
        if (!cancelled) setDailyVerse(data || null);
      } catch {
        if (!cancelled) setDailyVerse(null);
      } finally {
        if (!cancelled) setDailyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [translation]);

  const bookName = useMemo(() => {
    if (!dailyVerse?.bookId) return '';
    return (
      resolveHomeBookName(dailyVerse.bookId, books, dailyVerse.bookName) ||
      `Книга ${dailyVerse.bookId}`
    );
  }, [dailyVerse, books]);

  const verseNum = dailyVerse?.verse ? parseInt(dailyVerse.verse, 10) : 1;

  const versePreview = dailyVerse?.text ? trimVersePreview(dailyVerse.text) : '';

  const hasPassage = Boolean(
    dailyVerse?.bookId && dailyVerse?.chapter && (versePreview || dailyVerse?.text)
  );

  const verseDayKey = dailyVerse?.date ? `${dailyVerse.date}:${translation}` : null;

  useEffect(() => {
    if (!dailyVerse?.bookId || !dailyVerse?.chapter || !dailyVerse?.verse) return;
    prefetchGuestVerseInsights({
      bookId: parseInt(dailyVerse.bookId, 10),
      chapter: parseInt(dailyVerse.chapter, 10),
      verse: parseInt(dailyVerse.verse, 10) || 1,
      translation,
      modes: ['simple'],
    });
  }, [dailyVerse?.bookId, dailyVerse?.chapter, dailyVerse?.verse, translation]);

  const navigateToBookmarks = () => {
    setShowMenu(false);
    if (dailyVerse?.bookId) {
      navigate(`/${translation}/${dailyVerse.bookId}/${dailyVerse.chapter || 1}`);
    } else {
      navigate(`/${translation}/1/1`);
    }
  };

  const handleOpenPassage = () => {
    bfHaptic('light');
    if (hasPassage && dailyVerse) {
      const v = dailyVerse.verse ? `/${dailyVerse.verse}` : '';
      navigate(`/${translation}/${dailyVerse.bookId}/${dailyVerse.chapter}${v}`);
      return;
    }
    setShowChapterSelector(true);
  };

  const handleInsight = (mode) => {
    if (!hasPassage || !dailyVerse?.bookId) return;
    bfHaptic('light');
    setInsightMode(mode);
  };

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 6) return 'Тихая ночь';
    if (h < 11) return 'Доброе утро';
    if (h < 17) return 'Добрый день';
    if (h < 22) return 'Тёплый вечер';
    return 'Спокойной ночи';
  }, []);

  return (
    <div className="bf-app bf-app--home">
      <BfHomeVideoBackdrop />

      <BfHeader
        bookName="Библия"
        chapter=""
        hidden={hidden}
        scrolled={scrolled}
        onOpenChapterSelector={() => setShowChapterSelector(true)}
        onOpenSearch={onOpenSearch}
        onOpenBookmarks={navigateToBookmarks}
        onOpenMenu={() => setShowMenu(true)}
      />

      <main className="bf-home bf-home--compact">
        <BfHomeSpot
          greeting={greeting}
          bookName={bookName}
          chapter={dailyVerse?.chapter}
          verse={dailyVerse?.verse ?? verseNum}
          versePreview={versePreview}
          bookId={dailyVerse?.bookId ? parseInt(dailyVerse.bookId, 10) : null}
          translation={translation}
          loading={dailyLoading}
          hasPassage={hasPassage}
          insightMode={insightMode}
          onInsightModeChange={setInsightMode}
          onOpenPassage={handleOpenPassage}
          onOpenInsight={handleInsight}
        />
      </main>

      {showChapterSelector && (
        <BfChapterSelector
          bookId={dailyVerse?.bookId || 1}
          currentChapter={dailyVerse?.chapter || 1}
          totalChapters={50}
          bookName={bookName || ''}
          books={books}
          onSelect={(bId, chap) => {
            setShowChapterSelector(false);
            navigate(`/${translation}/${bId}/${chap}`);
          }}
          onClose={() => setShowChapterSelector(false)}
        />
      )}

      {showMenu && (
        <BfMenuSheet
          onClose={() => setShowMenu(false)}
          onSelectChapter={() => {
            setShowMenu(false);
            setShowChapterSelector(true);
          }}
          onOpenSearch={
            onOpenSearch
              ? () => {
                  setShowMenu(false);
                  onOpenSearch();
                }
              : undefined
          }
          onShowBookmarks={navigateToBookmarks}
          onGoSettings={() => {
            setShowMenu(false);
            navigate('/settings');
          }}
        />
      )}
    </div>
  );
}
